// جرس الإشعارات (Phase 7A) — يعرض ما يولّده الخادم أصلًا، ولا يولّد شيئًا.
//
// الإشعارات تُنشأ في قاعدة البيانات منذ 0007 و0010 (تغيّر السعر/التكلفة، تغيّر نوع العملية،
// أحداث الدفعات) وتُسلَّم بقناة IN_APP، لكن لم تكن لها واجهة — فكانت تنبيهات حرجة تتراكم بلا
// من يراها. هذه الشاشة تفتحها، ولا تمسّ ما يولّدها ولا متى.
//
// ولا قناة أخرى هنا: Push وواتساب مؤجَّلتان بقرار المالك (7B/7C)، فلا رمز لهما في هذا الملف.
//
// **وسحبٌ كل دقيقة لأجل الصوت** (قرار المالك: «محتاج أسمع الإشعار»): كان لا استقصاء أصلًا،
// فلم يكن للنظام لحظةٌ يعرف فيها أن إشعارًا جديدًا وصل. فصار نداءً واحدًا كل ٦٠ ثانية —
// قراءةٌ واحدة لا أثر لها على الخادم — وإن زاد عدد غير المقروء عمّا كان، سُمعت نغمة.
// والنغمة تُولَّد في المتصفّح بلا ملف، والاختيار للمستخدم ويبقى في جهازه.
//
// الدرج يفتح من **الحافة اليمنى الفعلية** (مؤشرات الأداء من اليسار) — قرار عرض لا اتجاه لغة.
// والتصفية بالتبويبات محلية بحتة على ما حُمّل: لا مسار جديد ولا مرشّح على الخادم.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, get, post, qs, type AppNotification, type NotificationsResponse } from '../lib/api.ts';
import { Alert, Badge, Drawer, Empty, Modal, Spinner, TextInput } from '../components/ui.tsx';
import { cairoDateTimeAr } from '../lib/format.ts';
import { SEVERITY_AR, SEVERITY_TONE } from './notificationLabels.ts';
import { TONES, currentTone, playNotificationSound, setSoundEnabled, setSoundPitch, setSoundVolume, setTone, soundEnabled, soundPitch, soundVolume } from './notificationSound.ts';
import { ensurePushSubscription, requestSystemNotify, systemNotify, systemNotifyState, type SystemNotifyState } from './systemNotify.ts';
import { syncDelivered } from '../lib/nativePush.ts';

/** اشتراك هذا المتصفّح بإشعارات الجهاز (0145) — يُجدَّد عند كل فتح */
const subscribePush = () => ensurePushSubscription(
  async () => (await get<{ publicKey: string }>('/push/public-key')).publicKey,
  sub => post('/me/push', sub),
);
import { usePulse } from '../lib/pulse.tsx';
import { NotificationPopup } from './NotificationPopup.tsx';
import { areaLabelAr, googleMapsUrl, officeDistanceAr, type Office } from '../lib/geoAreas.ts';
import { useOffices } from '../lib/useOffices.ts';
import { FileViewer } from '../booking/attachments/FileViewer.tsx';
import { openGuideArticle } from '../guide/guideLink.ts';
import { requestOpen } from '../lib/openIntent.ts';

// الدرج يفتح على **غير المقروء** وحده (قرار المالك): ما وُسم مقروءًا يختفي من القائمة فورًا
// ولا يبقى أمامه، ويظلّ في «المقروءة» — أرشيفًا لا واجهةً — لأن القاعدة لا تحذف إشعارًا.
type Tab = 'unread' | 'read';
const TABS: { id: Tab; ar: string }[] = [
  { id: 'unread', ar: 'الجديدة' },
  { id: 'read', ar: 'المقروءة' },
];

/**
 * موقع البصمة من الحمولة — **المنطقة بالاسم** ورابط الخريطة، وبلا إحداثيات في الشاشة (قرار المالك).
 * الاسم يُستنتج هنا من جدول الأحياء لا من خدمة خارجية، والرابط يُبنى هنا لا يُقرأ من الخادم.
 */
function TapLocation({ n, offices }: { n: AppNotification; offices: Office[] }) {
  if (n.type !== 'ATTENDANCE_TAP' || !n.payload) return null;
  const lat = n.payload.gpsLat, lon = n.payload.gpsLon;
  if (typeof lat !== 'number' || typeof lon !== 'number') {
    const reason = typeof n.payload.locationDeniedReason === 'string' ? n.payload.locationDeniedReason : null;
    return <p className="mt-1.5 text-sm text-amber-800">الموقع غير متاح{reason ? ` — ${reason}` : ''}</p>;
  }
  return (
    <p className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
      <span className="font-semibold text-ink-900">📍 {areaLabelAr(lat, lon)}</span>
      {/* وكم يبعد عن أقرب مكتب بالمتر (قرار المالك) — والنطاق من إعداد المكتب نفسه */}
      {officeDistanceAr(lat, lon, offices) && (
        <span className={officeDistanceAr(lat, lon, offices)!.startsWith('داخل') ? 'font-semibold text-emerald-800' : 'text-amber-800'}>
          {officeDistanceAr(lat, lon, offices)}
        </span>
      )}
      <a href={googleMapsUrl(lat, lon)} target="_blank" rel="noopener noreferrer"
        className="rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-900 hover:bg-emerald-100">
        افتح على خرائط جوجل
      </a>
    </p>
  );
}

/** رقم طلب الإجازة من حمولة الإشعار — به يُعتمد الطلب أو يُرفض من الدرج مباشرةً. */
function leaveRequestId(n: AppNotification): string | null {
  if (n.type !== 'LEAVE_REQUEST_PENDING' || !n.payload) return null;
  const id = n.payload.leaveRequestId;
  return typeof id === 'string' ? id : null;
}

/**
 * طلب سلفة في انتظار قرار (0091): معرّفه من الحمولة، وبه يُعتمد أو يُرفض من الدرج
 * كطلب الإجازة سواءً — ومن يقرأ الطلب في جرسه هو من يقرّره، بلا رحلةٍ إلى شاشة أخرى.
 */
function advanceOf(n: AppNotification): { id: string; amount: string; repayMonth: string } | null {
  if (n.type !== 'SALARY_ADVANCE_PENDING' || !n.payload) return null;
  const id = n.payload.advanceId;
  if (typeof id !== 'string') return null;
  return { id, amount: String(n.payload.amount ?? ''), repayMonth: String(n.payload.repayMonth ?? '') };
}

/**
 * طلب مصروف شريك في انتظار قرار المالك (0100): معرّف الطلب وحسابه من الحمولة — بهما
 * يُوافَق أو يُرفض من الجرس مباشرةً (قرار المالك: «خلّيني أوافق على المصروف من برّا
 * من الإشعارات»). والقرار للمالك وحده كما في شاشة الخزنة.
 */
function partnerExpenseOf(n: AppNotification): { id: string; accountId: string; amount: string } | null {
  if (n.type !== 'PARTNER_EXPENSE_PENDING' || !n.payload) return null;
  const id = n.payload.requestId, accountId = n.payload.accountId;
  if (typeof id !== 'string' || typeof accountId !== 'string') return null;
  return { id, accountId, amount: String(n.payload.amount ?? '') };
}

/**
 * تقييم Google Map في انتظار قرار: الحجز ومعرّف صورته من الحمولة — بهما تُفتح الصورة
 * ويُعتمد التقييم أو يُرفض من الدرج مباشرةً (قرار المالك).
 */
function ratingOf(n: AppNotification): { bookingId: string; evidenceId: string | null; bookingNo: string } | null {
  if (n.type !== 'RATING_PENDING_REVIEW' || !n.payload) return null;
  const id = n.payload.bookingId;
  if (typeof id !== 'string') return null;
  const ev = n.payload.ratingEvidenceId;
  const no = n.payload.bookingNo;
  return { bookingId: id, evidenceId: typeof ev === 'string' ? ev : null, bookingNo: typeof no === 'string' ? no : '' };
}

/**
 * دفعةٌ بانتظار المراجعة (0087) — معرّفها ومعرّف إثباتها من الحمولة، بهما تُفتح
 * الصورة وتُعتمد الدفعة **من الجرس** (قرار المالك: «خلّيني أعرف أشوف الصورة من
 * الإشعارات وأوافق على الدفعة عادي»).
 *
 * وكان الأدمن يقرأ الإشعار ثم يخرج إلى «تقرير التحصيلات» ويبحث عن رقم الدفعة فيه —
 * والقرار الذي يُؤجَّل خطوةً يُنسى، فتبقى دفعةٌ صحيحة معلّقةً أسبوعًا.
 *
 * **والإشعارات الأقدم بلا المفتاحين**: تُردّ `null` فتختفي أزرارها بدل أن تنكسر.
 */
function paymentOf(n: AppNotification): { paymentId: string; paymentNo: string } | null {
  if (n.type !== 'PAYMENT_PENDING_REVIEW' || !n.payload) return null;
  const id = n.payload.payment_id;
  if (typeof id !== 'string') return null;
  const no = n.payload.payment_no;
  return { paymentId: id, paymentNo: typeof no === 'string' ? no : '' };
}

/** إثباتٌ واحد كما يردّه الخادم ممّا يلزم هنا */
type PaymentProof = { id: string; originalFileName?: string | null; mimeType?: string | null; isCurrent?: boolean };
/** شكل ما يردّه `/admin/payments/:id` ممّا يلزم هنا: رقم النسخة والإثباتات. */
interface PaymentPeek {
  payment: { rowVersion: number; attachments?: PaymentProof[] };
}
const proofView = (a: PaymentProof, label: string) => ({
  path: `/files/payment-attachments/${a.id}`,
  name: a.originalFileName || label,
  mimeType: a.mimeType || 'image/*',
});

/**
 * طلب مراجعة تسعير من «دليلك في السياحة» (0075): يردّ عليه الأدمن من الدرج نفسه بالسعر
 * والتفاصيل مختصرةً، فيصل الردُّ جرسَ صاحب الطلب — بدل أن يخرج الجواب إلى الواتساب فيضيع.
 */
const isPriceReview = (n: AppNotification) => n.type === 'GUIDE_PRICE_REVIEW';

/**
 * **أي إشعارٍ من الدليل يفتح موضوعه** (المالك 04/10/2026: «اى اشعار من الدليل يفتح من الموبيل او ال web»): موضوعٌ جديد
 * (`article_id`) أو طلب مراجعة وردّه (`guideArticleId` منذ 0075) — مفتاحان لتاريخٍ مختلف، والزرّ واحد.
 */
function guideArticleOf(n: AppNotification): string | null {
  if (!String(n.type ?? '').startsWith('GUIDE_')) return null;
  const id = n.payload?.article_id ?? n.payload?.guideArticleId;
  return typeof id === 'string' && /^[0-9a-f-]{36}$/i.test(id) ? id : null;
}

/** نصّ الإشعار — وإشعارات البصمة الأقدم كانت تحمل الإحداثيات في نصّها؛ تُقصّ هنا فلا ضوضاء. */
function bodyOf(n: AppNotification): string | null {
  if (!n.body) return null;
  return n.type === 'ATTENDANCE_TAP' ? n.body.replace(/ · الموقع.*$/, '.') : n.body;
}

/** أيقونة حسب الخطورة — تمييز بصري، والنصّ العربي هو المرجع. */
const SEVERITY_ICON: Record<string, string> = { CRITICAL: '⛔', WARNING: '⚠️', INFO: 'ℹ️' };

/**
 * : هل لصاحب الجلسة أن يعتمد طلب الإجازة أو يرفضه من الدرج؟ يُمرَّر من
 * الشاشة التي تعرف الدور — والخادم هو الحارس على كل حال (403 لغير الأدمن).
 */
export function NotificationsBell({ canDecideLeave = false, canDecideRating = false, canDecidePartnerExpense = false, compact = false }:
  /** `compact`: الجرس وحده بلا مفتاحَي الصوت وتنبيهات النظام — لترويسة الموبايل (06/10/2026)؛ النغمة تُضبط من داخل اللوح */
  { canDecideLeave?: boolean; canDecideRating?: boolean; canDecidePartnerExpense?: boolean; compact?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('unread');
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** «حذف الكل» بضغطتين: الأولى تسأل والثانية تنفّذ — كبقية الحذف في البرنامج (بلا كتابة) */
  const [confirmAll, setConfirmAll] = useState(false);
  const [sound, setSound] = useState(() => soundEnabled());
  const [sysNotify, setSysNotify] = useState<SystemNotifyState>(() => systemNotifyState());
  /**
   * **لماذا لا يعمل؟ يُقال صراحةً** (قرار المالك 01/10/2026: «ليه تنبيهات النظام ضلمة ومبتشغلش عندي»): المتصفّح الذي
   * مُنع فيه الإذن مرّةً لا يسأل ثانيةً — فكان الزرّ يُضغط ولا يحدث شيء، والسبب في تلميحٍ لا يُرى إلا بالمرور فوقه.
   * الآن: الممنوع زرٌّ برتقاليّ «التنبيهات ممنوعة»، وكل محاولةٍ لم تنجح تفتح خطوات السماح بها في المتصفّح والويندوز.
   */
  const [sysHelp, setSysHelp] = useState(false);
  const enableSystemNotify = () => {
    if (sysNotify === 'denied') { setSysHelp(true); return; }
    void requestSystemNotify().then(st => {
      setSysNotify(st);
      if (st === 'granted') {
        void subscribePush();
        systemNotify('iConTravel', 'تنبيهات النظام شغّالة — هتوصلك وأنت في برنامج تاني.', 'welcome');
      } else setSysHelp(true);
    });
  };
  // أُذن من قبل؟ يُجدَّد الاشتراك بصمت عند الفتح — فتصل التنبيهات والتبويب مقفول
  useEffect(() => { if (systemNotifyState() === 'granted') void subscribePush(); }, []);
  const [tone, setToneId] = useState(() => currentTone().id);
  /** علوّ الصوت ودرجته (01/10/2026) — يُسمعان عند ترك الشريط */
  const [volume, setVolumeState] = useState(() => soundVolume());
  const [pitch, setPitchState] = useState(() => soundPitch());
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [deciding, setDeciding] = useState<string | null>(null);
  const admin = canDecideLeave;
  /**
   * الملفّ المعروض — **بمساره كاملًا** لا بمعرّفه وحده: إثبات الدفعة يُخدَم من
   * `/files/payment-attachments/:id` وصورةُ التقييم من `/files/rating-evidence/:id`،
   * ومسارٌ واحدٌ لهما يجعل أحدَهما «الملف غير موجود» دائمًا.
   */
  const [view, setView] = useState<{ path: string; name: string; mimeType: string } | null>(null);
  /**
   * إثباتات الدفعة السارية بعد جلبها — **عدّة لا واحد** (مراجعة 24/09/2026): منذ «إثبات الدفع صورٌ عدّة»
   * (قرار المالك 23/09) يرفع البياع صورًا كلٌّ منها مرفقٌ مستقلّ، وكان الجرس يُظهر آخرها وحده.
   * المفتاح رقم الدفعة كما في `reasons`.
   */
  const [proofs, setProofs] = useState<Record<string, PaymentProof[]>>({});
  const seenUnread = useRef<number | null>(null);
  // ما ظهر في بطاقةٍ مفاجئة — فلا يُعاد على صاحبه كلّما حُمّلت القائمة
  const popped = useRef<Set<string>>(new Set());
  const [popups, setPopups] = useState<AppNotification[]>([]);
  const offices = useOffices();

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setError(null);
    try {
      const r = await get<NotificationsResponse>(`/notifications${qs({ limit: 50 })}`);
      setData(r);
      // إشعارٌ جديد وصل بين نداءين ⇒ نغمة. وأول قراءة لا تُصدر صوتًا: ليست جديدًا بل حالةً قائمة
      // الجديد = غير مقروءٍ لم يُعرض بعد — **بالمعرّف لا بالعدد** (01/10/2026): قراءةُ إشعارٍ ووصولُ آخر في النبضة
      // نفسها كانا يتعادلان فلا يُسمع شيء
      const fresh = seenUnread.current === null ? []
        : r.items.filter(n => n.readAt === null && !n.dismissedAt && !popped.current.has(n.id));
      if (fresh.length > 0) {
        playNotificationSound();
        // **وبطاقةٌ مفاجئة مع النغمة** (قرار المالك): غير المقروء الذي لم يُعرض بعد.
        // وأوّل قراءةٍ للجلسة لا تُظهر شيئًا — ما كان قائمًا ليس جديدًا.
        for (const n of fresh) popped.current.add(n.id);
        if (fresh.length > 0) setPopups(fresh);
        // وتنبيهُ النظام (ويندوز/ماك) حين تكون الصفحة خلف غيرها — قرار المالك 01/10/2026
        for (const n of fresh) systemNotify(n.title, n.body ?? '', n.id);
      } else if (seenUnread.current === null) {
        // الجلسة الأولى: تُعلَّم القائمة كلّها معروضةً بلا أن تُعرض
        for (const n of r.items) popped.current.add(n.id);
      }
      seenUnread.current = r.unreadCount;
    } catch (e) { if (!quiet) setError(e instanceof ApiError ? e.message : 'تعذّر تحميل الإشعارات'); }
  }, []);

  // مرّة عند فتح الجلسة، ثم حين تقول النبضة إن إشعارًا تغيّر (27/09/2026 — لا كل دقيقة على الفاضي)،
  // ثم عند فتح الدرج. وبلا نبضة (الاختبارات القديمة) كل دقيقة كما كان
  const pulse = usePulse();
  const hasPulse = pulse !== null && pulse.healthy;   // نبضةٌ فاشلة = بلا نبضة: يعود السؤال القديم (مراجعة 27/09/2026)
  useEffect(() => {
    void load();
    if (hasPulse) return;
    const id = setInterval(() => { void load(true); }, 60_000);
    return () => clearInterval(id);
  }, [load, hasPulse]);
  const pulseKey = pulse?.key.notifications ?? '';
  useEffect(() => { if (pulseKey) void load(true); }, [pulseKey, load]);

  const unread = data?.unreadCount ?? 0;
  /**
   * **موبايل التطبيق: ما شوهد يخرج من شريط الإشعارات** (المالك 03/10/2026: «لما يشوف الإشعار العدد يقل») — المقروء،
   * وكل ما ظهر في الدرج وهو مفتوح. والشات له حسابه في App (team-chat).
   */
  useEffect(() => {
    if (!data) return;
    const unseen = new Set(data.items.filter(n => n.readAt === null && !n.dismissedAt).map(n => n.id));
    if (open) unseen.clear();
    void syncDelivered(tag => tag === undefined || tag.startsWith('team-chat') || unseen.has(tag));
  }, [data, open]);
  const items = data?.items ?? [];
  const shown = useMemo(() => items.filter(n => (tab === 'unread' ? n.readAt === null : n.readAt !== null)), [items, tab]);

  /**
   * اعتماد طلب الإجازة أو رفضه **من الإشعار** (قرار المالك) — نفس مسار شاشة «الإجازات»
   * (admin/leave/:id/approve|reject) لا مسارًا جديدًا، ونفس قاعدته: الرفض يلزمه سبب.
   * وبعد القرار يُوسم الإشعار مقروءًا فيختفي من «الجديدة»: القرار اتُّخذ فلا ينتظر شيئًا.
   */
  /** ردّ الأدمن على طلب تسعير: يصل صاحب الطلب وحده، والطلب يُوسَم مقروءًا على الخادم. */
  async function sendPriceReply(n: AppNotification): Promise<void> {
    const text = (reasons[n.id] ?? '').trim();
    if (text.length < 2) { setError('اكتب الردّ أولًا — السعر والتفاصيل مختصرة.'); return; }
    setDeciding(n.id); setError(null);
    try {
      await post(`/guide/price-review/${n.id}/reply`, { text });
      setReasons(p => ({ ...p, [n.id]: '' }));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر إرسال الردّ');
    } finally { setDeciding(null); }
  }

  async function decideLeave(n: AppNotification, decision: 'approve' | 'reject'): Promise<void> {
    const id = leaveRequestId(n);
    if (!id) return;
    const reason = (reasons[id] ?? '').trim();
    if (decision === 'reject' && reason === '') { setError('الرفض يلزمه سبب — اكتبه أولًا.'); return; }
    setDeciding(n.id); setError(null);
    try {
      await post(`/admin/leave/${id}/${decision}`, decision === 'reject' ? { reason } : (reason === '' ? {} : { reason }));
      if (n.readAt === null) await post(`/notifications/${n.id}/read`, {});
      setReasons(p => ({ ...p, [id]: '' }));
      await load();
    } catch (e) {
      // أكثر أسباب الرفض شيوعًا أن القرار اتُّخذ من شاشة الإجازات قبل لحظات
      setError(e instanceof ApiError ? e.message : 'تعذّر تسجيل القرار');
      await load();
    } finally { setDeciding(null); }
  }

  /** قرار السلفة من الدرج — نفس قاعدة الإجازة: الرفض يلزمه سبب مكتوب. */
  async function decideAdvance(n: AppNotification, decision: 'approve' | 'reject'): Promise<void> {
    const a = advanceOf(n);
    if (!a) return;
    const reason = (reasons[a.id] ?? '').trim();
    if (decision === 'reject' && reason === '') { setError('الرفض يلزمه سبب — اكتبه أولًا.'); return; }
    setDeciding(n.id); setError(null);
    try {
      await post(`/admin/advances/${a.id}/${decision}`, decision === 'reject' ? { reason } : {});
      if (n.readAt === null) await post(`/notifications/${n.id}/read`, {});
      setReasons(p => ({ ...p, [a.id]: '' }));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر تسجيل القرار');
      await load();
    } finally { setDeciding(null); }
  }

  /** قرار طلب مصروف الشريك من الدرج: نفس مسار الخزنة (0100)، والرفض يلزمه سبب. */
  async function decidePartnerExpense(n: AppNotification, decision: 'APPROVE' | 'REJECT'): Promise<void> {
    const r = partnerExpenseOf(n);
    if (!r) return;
    const reason = (reasons[r.id] ?? '').trim();
    if (decision === 'REJECT' && reason === '') { setError('الرفض يلزمه سبب — اكتبه أولًا.'); return; }
    setDeciding(n.id); setError(null);
    try {
      await post(`/treasury/partners/${r.accountId}/requests/${r.id}/decide`, decision === 'REJECT' ? { decision, reason } : { decision });
      if (n.readAt === null) await post(`/notifications/${n.id}/read`, {});
      setReasons(p => ({ ...p, [r.id]: '' }));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر تسجيل القرار');
      await load();
    } finally { setDeciding(null); }
  }

  /**
   * قرار التقييم من الدرج: نفس مسار شاشة الاعتماد لا مسارًا جديدًا، ونفس قاعدته —
   * الرفض يلزمه سبب. وبعد القرار يُوسم الإشعار مقروءًا فيختفي من «الجديدة».
   */
  async function decideRating(n: AppNotification, status: 'APPROVED' | 'REJECTED'): Promise<void> {
    const r = ratingOf(n);
    if (!r) return;
    const reason = (reasons[r.bookingId] ?? '').trim();
    if (status === 'REJECTED' && reason === '') { setError('الرفض يلزمه سبب — اكتبه أولًا.'); return; }
    setDeciding(n.id); setError(null);
    try {
      await post(`/admin/ratings/${r.bookingId}/decision`, { status, reason: reason || null });
      if (n.readAt === null) await post(`/notifications/${n.id}/read`, {});
      setReasons(p => ({ ...p, [r.bookingId]: '' }));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر تسجيل القرار');
      await load();
    } finally { setDeciding(null); }
  }

  /**
   * فتح صورة إثبات الدفعة من الجرس (0087).
   *
   * **والمرفقات تُقرأ الآن لا من حمولة الإشعار**: الإثبات يُرفع بعد الدفعة، فالإشعار
   * وُلد قبله — وجدولُ الإشعارات محميٌّ ضد التعديل فلا يُكتب فيه لاحقًا. والقراءة
   * وقت الفتح تعطي حال الدفعة **الآن**: لو رُفعت الصورة بعد وصول الإشعار ظهرت.
   */
  async function showPaymentEvidence(n: AppNotification): Promise<void> {
    const p = paymentOf(n);
    if (!p) return;
    setDeciding(n.id); setError(null);
    try {
      const cur = await get<PaymentPeek>(`/admin/payments/${p.paymentId}`);
      // السارية كلّها لا آخرها — والنسخ المستبدَلة (إثباتٌ بديل) تُخفى كما في نافذة المراجعة
      const current = (cur.payment.attachments ?? []).filter(x => x.isCurrent !== false);
      if (current.length === 0) { setError('مفيش صورة إثبات مرفوعة على الدفعة دي.'); return; }
      setProofs(prev => ({ ...prev, [p.paymentId]: current }));
      // إثباتٌ واحد يُفتح مباشرةً كما كان — لا ضغطة زائدة على الحالة الأشيع
      if (current.length === 1) setView(proofView(current[0], `إثبات ${p.paymentNo}`));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر فتح صورة الإثبات');
    } finally { setDeciding(null); }
  }

  /**
   * اعتماد الدفعة أو رفضها من الجرس (0087).
   *
   * **ويُقرأ رقم النسخة قبل الكتابة**: مسار المراجعة يشترط `If-Match` حمايةً من أن
   * يراجعها أدمنان معًا. والحمولة لا تحمله (تقادمه لحظيّ)، فيُقرأ الآن ثم يُرسَل —
   * ولو سبقنا غيرُنا ردّ الخادم «تمت مراجعتها من مستخدم آخر» بدل أن يكتب فوق قراره.
   */
  async function decidePayment(n: AppNotification, action: 'verify' | 'reject'): Promise<void> {
    const p = paymentOf(n);
    if (!p) return;
    const reason = (reasons[p.paymentId] ?? '').trim();
    if (action === 'reject' && reason === '') { setError('الرفض يلزمه سبب — اكتبه أولًا.'); return; }
    setDeciding(n.id); setError(null);
    try {
      const cur = await get<PaymentPeek>(`/admin/payments/${p.paymentId}`);
      await post(`/admin/payments/${p.paymentId}/${action}`,
        action === 'reject' ? { reason } : {},
        { 'if-match': String(cur.payment.rowVersion) });
      if (n.readAt === null) await post(`/notifications/${n.id}/read`, {});
      setReasons(prev => ({ ...prev, [p.paymentId]: '' }));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر تسجيل القرار على الدفعة');
      await load();
    } finally { setDeciding(null); }
  }

  async function markRead(n: AppNotification): Promise<void> {
    if (n.readAt !== null) return;
    setBusy(n.id);
    try { await post(`/notifications/${n.id}/read`, {}); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر وسم الإشعار كمقروء'); }
    finally { setBusy(null); }
  }

  /**
   * حذف الإشعار من درج صاحبه (قرار المالك: «أي حد يقدر يحذف إشعار عادي»).
   *
   * وهو **إخفاءٌ لا محو**: الصفّ يبقى في القاعدة (`notifications_no_delete` منذ 0007) فسجل
   * الإدارة كامل ولا يضيع أثر ما أُرسل — لكنه يختفي من درج من حذفه وحده، ونسخة زميله لا
   * تُمسّ. والخادم يشترط أن يكون مقروءًا أولًا، فيُوسَم هنا ثم يُخفى في خطوةٍ واحدة أمام
   * المستخدم — لا نقرتين لفعلٍ واحد في ذهنه.
   */
  async function remove(n: AppNotification): Promise<void> {
    setBusy(n.id); setError(null);
    try {
      if (n.readAt === null) await post(`/notifications/${n.id}/read`, {});
      await post(`/notifications/${n.id}/dismiss`, {});
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'تعذّر حذف الإشعار');
    } finally { setBusy(null); }
  }

  /**
   * **حذف الكل** (قرار المالك 23/09/2026: «زرار يخلينى امسح الاشعارات دى كلها مره واحده … لكل الحسابات»):
   * لكل حساب على إشعاراته هو — إخفاءٌ لا محو كالحذف الواحد، والخادم يسمها مقروءة في الجملة نفسها.
   */
  async function removeAll(): Promise<void> {
    if (!confirmAll) { setConfirmAll(true); return; }
    setBusy('all'); setError(null);
    try { await post('/notifications/dismiss-all', {}); setConfirmAll(false); await load(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'تعذّر حذف الإشعارات'); }
    finally { setBusy(null); }
  }

  /** إغلاق البطاقة يسمها مقروءة — فلا تُعاد، ولا يبقى عدّادٌ يقول غير ما رأى. */
  async function closePopup(n: AppNotification) {
    setPopups(list => list.filter(x => x.id !== n.id));
    try { if (n.readAt === null) { await post(`/notifications/${n.id}/read`, {}); await load(true); } }
    catch { /* الوسم مساعدٌ: فشلُه لا يمنع إغلاق البطاقة */ }
  }

  return (
    <>
      <NotificationPopup items={popups} onClose={n => void closePopup(n)} />

      {sysHelp && (
        <Modal title="إزاي أشغّل تنبيهات النظام" size="sm" onClose={() => setSysHelp(false)}>
          <div className="space-y-3 text-sm leading-7 text-ink-800">
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
              المتصفّح على الجهاز ده <b>مانع التنبيهات</b> لموقع البرنامج — ولمّا يتمنع مرّة، مبيسألش تاني لوحده.
            </p>
            <p className="font-bold text-ink-900">١) اسمح بيها في المتصفّح (Chrome أو Edge):</p>
            <ol className="list-decimal space-y-1 ps-6">
              <li>اضغط على علامة <b>🔒 القفل</b> (أو <b>⚙︎</b>) اللي على يسار عنوان الموقع فوق.</li>
              <li>اختار <b>الإشعارات / Notifications</b> وخلّيها <b>سماح / Allow</b>.</li>
              <li>اعمل <b>تحديث للصفحة (F5)</b>، وبعدين اضغط «تنبيهات النظام» تاني.</li>
            </ol>
            <p className="font-bold text-ink-900">٢) ولو لسه مش بتظهر — من الويندوز:</p>
            <ol className="list-decimal space-y-1 ps-6">
              <li>افتح <b>الإعدادات ← النظام ← الإشعارات</b>.</li>
              <li>خلّي <b>الإشعارات</b> شغّالة، وخلّي <b>Google Chrome</b> (أو <b>Microsoft Edge</b>) شغّال تحتها.</li>
              <li>اقفل <b>عدم الإزعاج / Focus assist</b> لو مفتوح.</li>
            </ol>
            <p className="text-xs text-ink-500">ده إعدادٌ على كل جهاز لوحده — والصوت والجرس جوّه البرنامج شغّالين في كل الأحوال.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-primary px-4 py-2 text-sm"
                onClick={() => { setSysNotify(systemNotifyState()); if (systemNotifyState() === 'granted') { setSysHelp(false); enableSystemNotify(); } else window.location.reload(); }}>
                سمحت بيها — جرّب تاني
              </button>
              <button type="button" className="btn-ghost px-4 py-2 text-sm" onClick={() => setSysHelp(false)}>قفل</button>
            </div>
          </div>
        </Modal>
      )}

      {/* مفتاح الصوت — تفضيل جهازٍ يبقى فيه، والتجربة تُسمعه فورًا وتفتح إذن الصوت في المتصفّح */}
      {!compact && sysNotify !== 'unsupported' && (

        <button type="button"
          className={`btn-ghost px-2 py-1 text-xs ${sysNotify === 'granted' ? 'text-emerald-700' : sysNotify === 'denied' ? 'border-amber-400 bg-amber-50 font-semibold text-amber-800' : ''}`}
          title={sysNotify === 'granted' ? 'تنبيهات النظام مفعَّلة — تظهر على ويندوز وأنت في برنامج تاني' : sysNotify === 'denied' ? 'المتصفّح مانع التنبيهات — اضغط لخطوات السماح بها' : 'فعّل تنبيهات ويندوز/ماك'}
          aria-label={sysNotify === 'granted' ? 'تنبيهات النظام مفعَّلة' : sysNotify === 'denied' ? 'التنبيهات ممنوعة — إزاي أسمح بيها' : 'تفعيل تنبيهات النظام'}
          onClick={enableSystemNotify}>
          {sysNotify === 'granted' ? '🖥️ تنبيهات النظام ✓' : sysNotify === 'denied' ? '🔕 التنبيهات ممنوعة' : '🖥️ تنبيهات النظام'}
        </button>

      )}
      {!compact && <button type="button" className="btn-ghost px-2 py-1.5 text-sm"
        aria-label={sound ? 'نغمة التنبيه مفعَّلة — اضغط للكتم' : 'نغمة التنبيه مكتومة — اضغط للتفعيل'}
        title={sound ? 'نغمة التنبيه مفعَّلة' : 'نغمة التنبيه مكتومة'}
        onClick={() => { const on = !sound; setSound(on); setSoundEnabled(on); if (on) playNotificationSound(true); }}>
        <span aria-hidden="true">{sound ? '🔊' : '🔇'}</span>
      </button>}

      <button type="button" className="btn-ghost relative px-3 py-1.5 text-sm"
        aria-label={unread > 0 ? `الإشعارات — ${unread} غير مقروء` : 'الإشعارات'}
        onClick={() => { setOpen(true); void load(); }}>
        <span aria-hidden="true">🔔</span>
        {unread > 0 && (
          <span className="absolute -top-1 -start-1 min-w-5 rounded-full bg-rose-600 px-1 text-xs font-bold text-white tabular-nums">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <Drawer title="الإشعارات" side="right" onClose={() => setOpen(false)}>
          {/* التبويبات: تصفية محلية على ما حُمّل، وعدد غير المقروء من الخادم */}
          <nav className="flex gap-1 rounded-xl bg-white p-1 shadow-card" aria-label="تصفية الإشعارات">
            {TABS.map(t => (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                aria-current={tab === t.id ? 'true' : undefined}
                className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-medium ${
                  tab === t.id ? 'bg-brand-600 text-white' : 'text-ink-700 hover:bg-slate-100'}`}>
                {t.ar}
                {t.id === 'unread' && unread > 0 && (
                  <span className={`ms-1.5 rounded-full px-1.5 text-xs tabular-nums ${
                    tab === t.id ? 'bg-white/25' : 'bg-rose-600 text-white'}`}>{unread}</span>
                )}
              </button>
            ))}
          </nav>

          {/* اختيار النغمة بالأذن لا بالوصف (قرار المالك): الاختيار يعزفها ويحفظها لهذا الجهاز.
              **قائمةٌ منسدلة لا عشرون زرًّا** (قرار المالك 02/10/2026: «اختصر المنظر دا ف drop list بدل ما هما كتير كدا،
              وخلّيها نغمة الإشعار») — والفاخرة أوّلًا في مجموعتها (01/10/2026: «high end ولاكشري»)، و▶ يعيد سماعها. */}
          <div className="mt-3 rounded-xl border border-slate-200 p-3">
            <label className="block">
              <span className="text-xs font-semibold text-ink-700">نغمة الإشعار</span>
              <div className="mt-1 flex gap-2">
                <select className="input flex-1 py-1.5 text-sm" value={tone} aria-label="نغمة الإشعار"
                  onChange={e => {
                    const t = TONES.find(x => x.id === e.target.value);
                    if (t) { setTone(t.id); setToneId(t.id); playNotificationSound(true, t); }
                  }}>
                  <optgroup label="✨ فاخرة — بطابع آيفون">
                    {TONES.filter(t => t.luxe).map(t => <option key={t.id} value={t.id}>{t.ar}</option>)}
                  </optgroup>
                  <optgroup label="باقي النغمات">
                    {TONES.filter(t => !t.luxe).map(t => <option key={t.id} value={t.id}>{t.ar}</option>)}
                  </optgroup>
                </select>
                <button type="button" className="btn-ghost px-3 py-1 text-sm" aria-label="اسمع النغمة"
                  onClick={() => playNotificationSound(true, TONES.find(x => x.id === tone))}>▶ اسمع</button>
              </div>
            </label>
            {/* علوّ الصوت ودرجته (قرار المالك 01/10/2026: «عاوز تحكّم في درجة وعلوّ الصوت») */}
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-xs">
                <span className="flex items-center justify-between font-semibold text-ink-700">
                  <span>🔊 علوّ الصوت</span><span className="tabular-nums text-brand-700">{volume}٪</span>
                </span>
                <input type="range" min={10} max={100} step={5} value={volume} aria-label="علوّ الصوت"
                  className="mt-1 w-full accent-brand-600"
                  onChange={e => { const v = Number(e.target.value); setVolumeState(v); setSoundVolume(v); }}
                  onPointerUp={() => playNotificationSound(true)} onKeyUp={() => playNotificationSound(true)} />
              </label>
              <label className="block text-xs">
                <span className="flex items-center justify-between font-semibold text-ink-700">
                  <span>🎚️ درجة النغمة</span>
                  <span className="text-brand-700">{pitch === 0 ? 'عادية' : pitch < 0 ? `أعمق ${-pitch}` : `أحدّ ${pitch}`}</span>
                </span>
                <input type="range" min={-6} max={6} step={1} value={pitch} aria-label="درجة النغمة"
                  className="mt-1 w-full accent-brand-600"
                  onChange={e => { const v = Number(e.target.value); setPitchState(v); setSoundPitch(v); }}
                  onPointerUp={() => playNotificationSound(true)} onKeyUp={() => playNotificationSound(true)} />
                <span className="mt-0.5 flex justify-between text-[10px] text-ink-400"><span>أعمق</span><span>أحدّ</span></span>
              </label>
            </div>
          </div>

          {error && <div className="mt-3"><Alert>{error}</Alert></div>}

          {!data && !error ? <Spinner label="جارٍ تحميل الإشعارات…" /> : data && (
            shown.length === 0
              ? <Empty>{tab === 'unread' ? 'لا إشعارات جديدة.' : 'لا إشعارات مقروءة.'}</Empty>
              : (
                <ul className="mt-3 space-y-2">
                  {shown.map(n => (
                    <li key={n.id}
                      className={`rounded-xl border bg-white p-3 shadow-card ${
                        n.readAt === null ? 'border-brand-200' : 'border-slate-200 opacity-70'}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span aria-hidden="true">{SEVERITY_ICON[n.severity] ?? 'ℹ️'}</span>
                        <Badge tone={SEVERITY_TONE[n.severity]}>{SEVERITY_AR[n.severity]}</Badge>
                        <span className="font-semibold text-ink-900">{n.title}</span>
                        {n.readAt === null && <Badge tone="bg-brand-50 text-brand-700">جديد</Badge>}
                      </div>
                      {/* سطرٌ لكل خانة (01/10/2026) — والتعليق داخل {} لا نصًّا يظهر تحت كل إشعار (المراجعة الشاملة 02/10/2026) */}
                      {bodyOf(n) && <p className="mt-1.5 whitespace-pre-line text-sm leading-6 text-ink-700">{bodyOf(n)}</p>}
                      <TapLocation n={n} offices={offices} />

                      {/* قرار الإجازة من هنا — للأدمن وحده وللطلب الذي لم يُقرَّر بعد (قرار المالك) */}
                      {admin && leaveRequestId(n) && n.readAt === null && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <TextInput className="w-48 py-1 text-xs" placeholder="سبب الرفض (إلزامي للرفض)"
                            aria-label="سبب القرار"
                            value={reasons[leaveRequestId(n)!] ?? ''}
                            onChange={e => setReasons(p => ({ ...p, [leaveRequestId(n)!]: e.target.value }))} />
                          <button type="button" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decideLeave(n, 'approve')}>
                            اعتماد
                          </button>
                          <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decideLeave(n, 'reject')}>
                            رفض
                          </button>
                        </div>
                      )}
                      {/* ردّ التسعير من هنا — للأدمن وحده وللطلب الذي لم يُردّ عليه بعد (0075) */}
                      {admin && isPriceReview(n) && n.readAt === null && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <TextInput className="w-64 py-1 text-xs" placeholder="السعر والتفاصيل مختصرة…"
                            aria-label="ردّ على طلب المراجعة"
                            value={reasons[n.id] ?? ''}
                            onChange={e => setReasons(p => ({ ...p, [n.id]: e.target.value }))} />
                          <button type="button" className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void sendPriceReply(n)}>
                            {deciding === n.id ? 'جارٍ الإرسال…' : 'إرسال الردّ للبياع'}
                          </button>
                        </div>
                      )}
                      {/* مراجعة الدفعة من هنا — صورةُ إثباتها ثم القرار (0087، قرار المالك) */}
                      {admin && paymentOf(n) && n.readAt === null && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <button type="button" className="btn-ghost px-3 py-1.5 text-xs"
                            disabled={deciding === n.id} onClick={() => void showPaymentEvidence(n)}>
                            عرض صور الإثبات
                          </button>
                          {(proofs[paymentOf(n)!.paymentId]?.length ?? 0) > 1 && proofs[paymentOf(n)!.paymentId]!.map((a, i) => (
                            <button key={a.id} type="button" className="btn-ghost max-w-[12rem] truncate px-3 py-1.5 text-xs"
                              title={a.originalFileName ?? undefined}
                              onClick={() => setView(proofView(a, `إثبات ${paymentOf(n)!.paymentNo} (${i + 1})`))}>
                              إثبات {i + 1}{a.originalFileName ? ` — ${a.originalFileName}` : ''}
                            </button>
                          ))}
                          <TextInput className="w-48 py-1 text-xs" placeholder="سبب الرفض (إلزامي للرفض)"
                            aria-label="سبب قرار الدفعة"
                            value={reasons[paymentOf(n)!.paymentId] ?? ''}
                            onChange={e => setReasons(p => ({ ...p, [paymentOf(n)!.paymentId]: e.target.value }))} />
                          <button type="button" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decidePayment(n, 'verify')}>
                            {deciding === n.id ? 'جارٍ…' : 'اعتماد الدفعة'}
                          </button>
                          <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decidePayment(n, 'reject')}>
                            رفض
                          </button>
                        </div>
                      )}
                      {/* قرار السلفة من هنا — للأدمن وحده وللطلب الذي لم يُقرَّر بعد (0091) */}
                      {admin && advanceOf(n) && n.readAt === null && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <TextInput className="w-48 py-1 text-xs" placeholder="سبب الرفض (إلزامي للرفض)"
                            aria-label="سبب قرار السلفة"
                            value={reasons[advanceOf(n)!.id] ?? ''}
                            onChange={e => setReasons(p => ({ ...p, [advanceOf(n)!.id]: e.target.value }))} />
                          <button type="button" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decideAdvance(n, 'approve')}>
                            الموافقة على السلفة
                          </button>
                          <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decideAdvance(n, 'reject')}>
                            رفض
                          </button>
                        </div>
                      )}
                      {/* قرار طلب مصروف الشريك من هنا — للمالك وحده وللطلب الذي لم يُقرَّر بعد (0100) */}
                      {canDecidePartnerExpense && partnerExpenseOf(n) && n.readAt === null && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <TextInput className="w-48 py-1 text-xs" placeholder="سبب الرفض (إلزامي للرفض)"
                            aria-label="سبب قرار المصروف"
                            value={reasons[partnerExpenseOf(n)!.id] ?? ''}
                            onChange={e => setReasons(p => ({ ...p, [partnerExpenseOf(n)!.id]: e.target.value }))} />
                          <button type="button" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decidePartnerExpense(n, 'APPROVE')}>
                            الموافقة على المصروف
                          </button>
                          <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decidePartnerExpense(n, 'REJECT')}>
                            رفض
                          </button>
                        </div>
                      )}
                      {/* قرار تقييم Google Map من هنا — لمن يملك صلاحيته وحده (0068) */}
                      {canDecideRating && ratingOf(n) && n.readAt === null && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {ratingOf(n)!.evidenceId && (
                            <button type="button" className="btn-ghost px-3 py-1.5 text-xs"
                              onClick={() => setView({
                                path: `/files/rating-evidence/${ratingOf(n)!.evidenceId!}`,
                                name: `تقييم ${ratingOf(n)!.bookingNo}`,
                                mimeType: 'image/*',
                              })}>
                              عرض صورة التقييم
                            </button>
                          )}
                          <TextInput className="w-48 py-1 text-xs" placeholder="سبب الرفض (إلزامي للرفض)"
                            aria-label="سبب قرار التقييم"
                            value={reasons[ratingOf(n)!.bookingId] ?? ''}
                            onChange={e => setReasons(p => ({ ...p, [ratingOf(n)!.bookingId]: e.target.value }))} />
                          <button type="button" className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decideRating(n, 'APPROVED')}>
                            اعتماد
                          </button>
                          <button type="button" className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={deciding === n.id} onClick={() => void decideRating(n, 'REJECTED')}>
                            رفض
                          </button>
                        </div>
                      )}
                      {/* «📘 افتح الموضوع» (المالك 04/10/2026: «لما يشوفها من الاشعار ويضغط عليها توديه عليها علطول») */}
                      {/* توجيه على متابعة (0191): «افتح العميل» يروح للمتابعة نفسها */}
                      {String(n.type ?? '').startsWith('FOLLOW_UP_COACHING') && typeof n.payload?.followUpId === 'string' && (
                        <button type="button" className="mt-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
                          onClick={() => { void markRead(n); setOpen(false); requestOpen('crm', `follow-up:${String(n.payload?.followUpId)}`); try { window.location.hash = 'crm'; } catch { /* */ } }}>
                          📂 افتح العميل
                        </button>
                      )}
                      {/* مراجعة الإدارة للحجز (0190): «افتح الحجز» يروح للحجز نفسه */}
                      {(n.type === 'BOOKING_RETURNED' || n.type === 'BOOKING_RESUBMITTED' || n.type === 'BOOKING_APPROVED') && typeof n.payload?.bookingId === 'string' && (
                        <button type="button" className="mt-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
                          onClick={() => { void markRead(n); setOpen(false); requestOpen('bookings', `open:${String(n.payload?.bookingId)}`); try { window.location.hash = 'bookings'; } catch { /* */ } }}>
                          📂 افتح الحجز
                        </button>
                      )}
                      {/* «📣 فلان ذكرك» (0187): يفتح الشات على طول */}
                      {n.type === 'TEAM_MENTION' && (
                        <button type="button" className="mt-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
                          onClick={() => { void markRead(n); setOpen(false); try { window.location.hash = 'team-chat'; } catch { /* */ } }}>
                          💬 افتح الشات
                        </button>
                      )}
                      {guideArticleOf(n) && (
                        <button type="button" className="mt-2 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700"
                          onClick={() => { void markRead(n); setOpen(false); openGuideArticle(guideArticleOf(n)!); }}>
                          📘 افتح الموضوع
                        </button>
                      )}
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <span className="text-xs tabular-nums text-ink-500">{cairoDateTimeAr(n.createdAt)}</span>
                        <span className="flex items-center gap-1">
                          {n.readAt === null && (
                            <button type="button" className="btn-ghost px-3 py-1 text-xs"
                              disabled={busy === n.id} onClick={() => void markRead(n)}>
                              {busy === n.id ? 'جارٍ…' : 'وسم كمقروء'}
                            </button>
                          )}
                          {/* الحذف لكل صاحب إشعار (قرار المالك) — يختفي من درجه وحده */}
                          <button type="button" className="btn-ghost px-3 py-1 text-xs text-rose-700"
                            aria-label={`حذف الإشعار: ${n.title}`}
                            disabled={busy === n.id} onClick={() => void remove(n)}>
                            {busy === n.id ? 'جارٍ…' : 'حذف'}
                          </button>
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )
          )}

          <div className="mt-3 flex items-center justify-between gap-2">
            <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => { setTab('unread'); void load(); }}>
              تحديث
            </button>
            {items.length > 0 && (
              <span className="flex items-center gap-1.5">
                <button type="button" disabled={busy !== null} onClick={() => void removeAll()}
                  className={`btn-ghost px-3 py-1.5 text-sm text-rose-700 ${confirmAll ? 'border-rose-300 bg-rose-50' : ''}`}>
                  {busy === 'all' ? 'جارٍ…' : confirmAll ? 'تأكيد حذف الكل' : 'حذف الكل'}
                </button>
                {confirmAll && busy === null && (
                  <button type="button" autoFocus className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setConfirmAll(false)}>إلغاء</button>
                )}
              </span>
            )}
            <span className="text-xs tabular-nums text-ink-500">{items.length} إشعارًا محمَّلًا</span>
          </div>

          <p className="mt-2 text-xs text-ink-500">
            تصلك هنا تنبيهات النظام كما يولّدها الخادم. وسم الإشعار كمقروء يزيله من الجديدة ويبقيه في «المقروءة».
            {sound ? ' وتُسمع نغمة عند كل إشعار جديد.' : ' وصوت الإشعارات مكتوم على هذا الجهاز.'}
          </p>
        </Drawer>
      )}
      {view && (
        <FileViewer path={view.path} fileName={view.name}
          mimeType={view.mimeType} onClose={() => setView(null)} />
      )}
    </>
  );
}
